import { Section, SectionHeader } from "@impero-com/ui";
import { Select } from "antd";
import { Control, useFieldArray } from "react-hook-form";
import { z } from "zod";

import { RiskDirectoryBlessedFieldIds } from "api/generated/model";

import { HeaderButtons } from "./components/headerButtons";
import { StructureField, fieldSchema } from "./fields";
import { FieldSelectOption, fieldSelectOptions, newField } from "./formData";
import { FormSchema } from "./schema";
import { SectionKey } from "./sections";
import * as theme from "./theme.css";

export type SubsectionKey = `${SectionKey}.subsections.${number}`;
interface StructureSubsectionProps {
    sectionKey: SectionKey;
    subsectionKey: SubsectionKey;
    control: Control<FormSchema>;
    blessedFieldIds: RiskDirectoryBlessedFieldIds;
}

export const subsectionSchema = z.object({
    fields: z.array(fieldSchema),
});
export type RDStructureSubsection = z.infer<typeof subsectionSchema>;

export function StructureSubsection({
    sectionKey,
    subsectionKey,
    control,
    blessedFieldIds,
}: StructureSubsectionProps) {
    const { fields, append, remove, move } = useFieldArray({
        control,
        name: `${subsectionKey}.fields`,
    });

    const onSelectChange = (
        _value: string,
        option: FieldSelectOption | FieldSelectOption[]
    ) => {
        const field = newField(option as FieldSelectOption);
        append(field);
    };

    return (
        <div className={theme.sectionContent}>
            {fields.map((field, fieldIndex) => {
                const blessed = [
                    blessedFieldIds.inherentImpactId,
                    blessedFieldIds.inherentLikelihoodId,
                    blessedFieldIds.responseId,
                    blessedFieldIds.residualImpactId,
                    blessedFieldIds.residualLikelihoodId,
                ].includes(field.fieldId);
                return (
                    <Section styleVariant="secondary" key={field.id}>
                        <SectionHeader
                            titleLabel={field.name}
                            level={5}
                            showBorder={true}
                            rightChildren={
                                <div className={theme.fieldHeaderRight}>
                                    {blessed && (
                                        <div className={theme.blessedLabel}>
                                            BLESSED
                                        </div>
                                    )}
                                    <HeaderButtons
                                        onClickRemove={(index) => {
                                            remove(index);
                                        }}
                                        onChangeOrder={(index, newOrder) => {
                                            move(index, newOrder);
                                        }}
                                        index={fieldIndex}
                                        siblingsLength={fields.length}
                                        deleteDisabled={blessed}
                                    />
                                </div>
                            }
                        />
                        <StructureField
                            control={control}
                            field={field}
                            sectionKey={sectionKey}
                            fieldKey={`${subsectionKey}.fields.${fieldIndex}`}
                            blessedFieldIds={blessedFieldIds}
                        />
                    </Section>
                );
            })}
            <Select
                options={fieldSelectOptions}
                onChange={onSelectChange}
                placeholder="+ Add field"
                value={null}
            />
        </div>
    );
}
